import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { checkoutAPI } from '../lib/checkout-api';
import type { PayoutSimulatorSession, PayoutSimulatorState } from '../types/checkout';
import { formatAmount } from '../components/CheckoutStates';

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,var(--checkout-bg-top)_0%,var(--checkout-bg-bottom)_72%,#ffffff_100%)] px-4 py-5 text-slate-900">
      <div className="mx-auto max-w-sm">
        <div className="relative overflow-hidden rounded-[34px] bg-white/35 px-5 pb-7 pt-5 shadow-[0_24px_80px_rgba(86,102,220,0.18)] backdrop-blur">
          <div className="pointer-events-none absolute inset-x-[-12%] top-0 h-64 rounded-b-[38px] bg-[linear-gradient(180deg,var(--checkout-accent)_0%,var(--checkout-accent-strong)_100%)]" />
          <div className="relative">{children}</div>
        </div>
      </div>
    </div>
  );
}

function LoadingCard({ title = 'Payout' }: { title?: string }) {
  return (
    <Shell>
      <h1 className="text-center text-[2rem] font-extrabold tracking-tight text-white">{title}</h1>
      <div className="mt-10 rounded-[28px] bg-white px-6 py-12 text-center shadow-[0_18px_40px_rgba(79,91,191,0.14)]">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[var(--checkout-soft)] border-t-[var(--checkout-accent-strong)]" />
        <p className="mt-5 text-lg font-semibold text-slate-800">Loading payout...</p>
      </div>
    </Shell>
  );
}

function StatusCard({
  tone,
  title,
  description,
  session,
}: {
  tone: 'success' | 'failure' | 'expired' | 'invalid' | 'error';
  title: string;
  description: string;
  session?: PayoutSimulatorSession | null;
}) {
  const isSuccess = tone === 'success';
  const accent = isSuccess ? 'text-emerald-600' : tone === 'expired' ? 'text-orange-600' : 'text-red-600';
  const bg = isSuccess ? 'bg-emerald-100' : tone === 'expired' ? 'bg-orange-100' : 'bg-red-100';
  const iconPath = isSuccess
    ? 'M5 13l4 4L19 7'
    : 'M6 18L18 6M6 6l12 12';

  return (
    <Shell>
      <h1 className="text-center text-[2rem] font-extrabold tracking-tight text-white">Payout</h1>
      <div className="mt-10 rounded-[28px] bg-white px-6 py-10 text-center shadow-[0_18px_40px_rgba(79,91,191,0.14)]">
        <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${bg}`}>
          <svg className={`h-8 w-8 ${accent}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={iconPath} />
          </svg>
        </div>
        <h2 className="mt-5 text-2xl font-bold text-slate-900">{title}</h2>
        <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
        {session ? (
          <div className="mt-6 rounded-[20px] bg-slate-50 px-4 py-4 text-left">
            <p className="break-all text-sm text-slate-600">Order ID: {session.platform_order_id}</p>
            <p className="mt-1 break-all text-sm text-slate-600">Merchant Order: {session.merchant_order_no}</p>
            {session.tx_hash ? (
              <p className="mt-1 break-all text-sm font-semibold text-slate-800">Tx Hash: {session.tx_hash}</p>
            ) : session.utr ? (
              <p className="mt-1 break-all text-sm font-semibold text-slate-800">UTR: {session.utr}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Shell>
  );
}

function SimulatorCard({
  session,
  disabled,
  onSimulate,
}: {
  session: PayoutSimulatorSession;
  disabled: boolean;
  onSimulate: (status: 'SUCCESS' | 'FAILED') => void;
}) {
  return (
    <Shell>
      <h1 className="text-center text-[2rem] font-extrabold tracking-tight text-white">UAT Payout</h1>
      <div className="mt-8 rounded-[28px] bg-white px-6 py-8 text-center shadow-[0_18px_40px_rgba(79,91,191,0.14)]">
        <p className="text-[1.05rem] font-medium text-slate-700">Simulated payout amount</p>
        <p className="mt-3 text-[2.25rem] font-extrabold tracking-tight text-[var(--checkout-accent-strong)]">
          {formatAmount(session)}
        </p>
        <p className="mt-3 break-all text-sm font-medium text-slate-500">Order #{session.merchant_order_no}</p>
      </div>
      <div className="mt-5 rounded-[22px] bg-white/90 px-5 py-4 shadow-[0_14px_30px_rgba(79,91,191,0.10)]">
        <div className="grid gap-2 text-sm text-slate-600">
          <div className="flex justify-between gap-3">
            <span>Platform Order</span>
            <span className="min-w-0 flex-1 break-all text-right font-semibold text-slate-800">{session.platform_order_id}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span>Status</span>
            <span className="font-semibold text-slate-800">{session.payment_status}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span>Method</span>
            <span className="font-semibold text-slate-800">{session.payment_method_code}</span>
          </div>
        </div>
      </div>
      <div className="mt-6 grid gap-3">
        <button
          type="button"
          onClick={() => onSimulate('SUCCESS')}
          disabled={disabled}
          className="w-full rounded-full bg-emerald-600 px-6 py-4 text-lg font-bold text-white shadow-[0_14px_32px_rgba(5,150,105,0.24)] transition hover:bg-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none"
        >
          Simulate Payout Success
        </button>
        <button
          type="button"
          onClick={() => onSimulate('FAILED')}
          disabled={disabled}
          className="w-full rounded-full bg-red-600 px-6 py-4 text-lg font-bold text-white shadow-[0_14px_32px_rgba(220,38,38,0.20)] transition hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none"
        >
          Simulate Payout Failure
        </button>
      </div>
    </Shell>
  );
}

export default function PayoutSimulatorPage() {
  const { token } = useParams<{ token: string }>();
  const decodedToken = token ? decodeURIComponent(token) : '';
  const [state, setState] = useState<PayoutSimulatorState>('loading');
  const [session, setSession] = useState<PayoutSimulatorSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isSubmittingRef = useRef(false);

  const applySessionState = useCallback((sessionData: PayoutSimulatorSession) => {
    const expiresAt = new Date(sessionData.expires_at);
    setSession(sessionData);

    if (expiresAt < new Date()) {
      setState('expired');
    } else if (sessionData.payment_status === 'SUCCESS') {
      setState('success');
    } else if (sessionData.payment_status === 'FAILED') {
      setState('failure');
    } else {
      setState('simulator');
    }
  }, []);

  useEffect(() => {
    if (!decodedToken) {
      setState('invalid');
      return;
    }

    async function fetchSession() {
      try {
        const sessionData = await checkoutAPI.getPayoutSimulatorSession(decodedToken);
        applySessionState(sessionData);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load payout simulator';
        if (message === 'INVALID_TOKEN') {
          setState('invalid');
        } else {
          setError(message);
          setState('error');
        }
      }
    }

    void fetchSession();
  }, [decodedToken, applySessionState]);

  const handleSimulate = useCallback(async (status: 'SUCCESS' | 'FAILED') => {
    if (!session?.simulator_enabled || isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setState('submitting');
    try {
      const freshSession = await checkoutAPI.simulateUATManualPayout(decodedToken, status);
      applySessionState(freshSession);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to simulate payout';
      setError(message);
      setState('error');
    } finally {
      isSubmittingRef.current = false;
    }
  }, [applySessionState, decodedToken, session]);

  if (state === 'loading') {
    return <LoadingCard />;
  }
  if (state === 'submitting') {
    return <LoadingCard title="Processing" />;
  }
  if (state === 'simulator' && session) {
    return <SimulatorCard session={session} disabled={!session.simulator_enabled} onSimulate={handleSimulate} />;
  }
  if (state === 'success') {
    return (
      <StatusCard
        tone="success"
        title="Payout Successful"
        description={session ? `The payout of ${formatAmount(session)} has been completed.` : 'The payout has been completed.'}
        session={session}
      />
    );
  }
  if (state === 'failure') {
    return (
      <StatusCard
        tone="failure"
        title="Payout Failed"
        description="The payout was marked as failed."
        session={session}
      />
    );
  }
  if (state === 'expired') {
    return (
      <StatusCard
        tone="expired"
        title="Simulator Expired"
        description="This payout simulator link has expired."
        session={session}
      />
    );
  }
  if (state === 'invalid') {
    return (
      <StatusCard
        tone="invalid"
        title="Invalid Simulator Link"
        description="This payout simulator link is not valid."
        session={session}
      />
    );
  }
  return (
    <StatusCard
      tone="error"
      title="Something Went Wrong"
      description={error || 'An unknown error occurred'}
      session={session}
    />
  );
}
